/** Samostalni fuzzy skorer za pretragu proizvoda: kupac u zahtjevu za povrat upiše
 *  slobodan tekst ("Milano crne 42", "crne cizme milano br 42", "Milanno 42"), a mi ga
 *  poklapamo s nazivima proizvoda i varijacija iz kataloga. Bez zavisnosti od baze,
 *  mreže ili React-a — testabilno izolovano. */

const DIACRITICS: Record<string, string> = {
  č: "c",
  ć: "c",
  š: "s",
  ž: "z",
  đ: "d",
};

/** Normalizuje tekst za poređenje: lowercase, bez dijakritika, bez interpunkcije, sažeti razmaci. */
export function normalizeText(input: string): string {
  let text = input.toLowerCase();
  // đ ne prolazi kroz NFD normalizaciju pa se mapira eksplicitno prije nje.
  text = text.replace(/[čćšžđ]/g, (ch) => DIACRITICS[ch] ?? ch);
  text = text.normalize("NFD").replace(/[̀-ͯ]/g, "");
  text = text.replace(/[-/.,_()]/g, " ");
  text = text.replace(/\s+/g, " ").trim();
  return text;
}

/** Levenshtein razdaljina dva stringa, implementacija s dva reda bez zavisnosti.
 *  Ako je razlika u dužini veća od `maxDistance`, vraća `maxDistance + 1` bez računanja. */
function levenshtein(a: string, b: string, maxDistance: number): number {
  if (Math.abs(a.length - b.length) > maxDistance) return maxDistance + 1;

  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  let curr = new Array<number>(b.length + 1).fill(0);

  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j]! + 1,
        curr[j - 1]! + 1,
        prev[j - 1]! + cost,
      );
    }
    [prev, curr] = [curr, prev];
  }

  return prev[b.length]!;
}

/** Tolerancija Levenshtein razdaljine za dati token: 0 za kraće od 4 znaka,
 *  ≤1 za dužinu 4–6, ≤2 za dužinu ≥7. */
function levenshteinTolerance(tokenLength: number): number {
  if (tokenLength < 4) return 0;
  if (tokenLength <= 6) return 1;
  return 2;
}

/** Skor poklapanja jednog upitnog tokena naspram jednog kandidatskog tokena, 0..1. */
function tokenScore(queryToken: string, candidateToken: string): number {
  if (queryToken === candidateToken) return 1.0;

  if (queryToken.length >= 3 && candidateToken.startsWith(queryToken)) return 0.9;

  if (queryToken.length >= 3 && candidateToken.includes(queryToken)) return 0.75;

  const tolerance = levenshteinTolerance(queryToken.length);
  if (tolerance > 0 && levenshtein(queryToken, candidateToken, tolerance) <= tolerance) {
    return 0.6;
  }

  return 0;
}

/** Skor sličnosti upita i kandidata, 0..1. 0 = bez poklapanja. */
export function fuzzyScore(query: string, candidate: string): number {
  const queryTokens = normalizeText(query).split(" ").filter(Boolean);
  const candidateTokens = normalizeText(candidate).split(" ").filter(Boolean);

  if (queryTokens.length === 0 || candidateTokens.length === 0) return 0;

  let total = 0;
  let allExact = true;

  for (const queryToken of queryTokens) {
    let best = 0;
    for (const candidateToken of candidateTokens) {
      const score = tokenScore(queryToken, candidateToken);
      if (score > best) best = score;
    }
    if (best !== 1.0) allExact = false;
    total += best;
  }

  let score = total / queryTokens.length;
  if (allExact) score = Math.min(1.0, score + 0.05);

  return score;
}

/** Rangira kandidate opadajuće po skoru, odbacuje one ispod praga. */
export function fuzzyRank<T>(
  query: string,
  items: T[],
  toText: (item: T) => string,
  options?: { minScore?: number; limit?: number },
): { item: T; score: number }[] {
  const minScore = options?.minScore ?? 0.3;
  const limit = options?.limit ?? 20;

  const scored = items
    .map((item, index) => ({ item, score: fuzzyScore(query, toText(item)), index }))
    .filter((entry) => entry.score >= minScore);

  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.index - b.index;
  });

  return scored.slice(0, limit).map(({ item, score }) => ({ item, score }));
}

/** Najinformativniji token upita — koristi se kao `search` term za živi Woo API. */
export function strongestToken(query: string): string {
  const tokens = normalizeText(query).split(" ").filter(Boolean);
  if (tokens.length === 0) return "";

  const candidates = tokens.filter((token) => token.length >= 3 && !/^\d+$/.test(token));
  if (candidates.length === 0) return tokens[0]!;

  return candidates.reduce((longest, token) =>
    token.length > longest.length ? token : longest,
  );
}
